import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { FUEL } from '@wooorms/shared';
import { UIScaleManager } from '../utils/UIScaleManager';

/**
 * FuelBar configuration
 */
export interface FuelBarConfig {
  /** Bar width */
  width: number;
  /** Bar height */
  height: number;
  /** Padding from bottom of screen */
  bottomPadding: number;
}

const DEFAULT_CONFIG: FuelBarConfig = {
  width: 200,
  height: 16,
  bottomPadding: 40,
};

/** Fuel level thresholds for color changes */
const FUEL_THRESHOLDS = {
  /** Above this = high fuel (teal) */
  HIGH: 0.5,
  /** Above this = medium fuel (yellow), below = low fuel (red) */
  MEDIUM: 0.25,
} as const;

/** Label positioning offset from bar edge */
const LABEL_OFFSET = -30;

/**
 * FuelBar - PixiJS UI component for displaying boost fuel
 *
 * Displays a horizontal bar showing current fuel level
 * with visual feedback for low fuel states
 */
export class FuelBar {
  /** Main container */
  public container: Container;

  /** Background graphics */
  private background: Graphics;

  /** Fuel fill graphics */
  private fill: Graphics;

  /** Fuel border */
  private border: Graphics;

  /** Fuel icon/label */
  private label: Text;

  /** Configuration */
  private config: FuelBarConfig;

  /** Current fuel level (0-100) */
  private currentFuel: number = FUEL.MAX_FUEL;

  /** UI Scale Manager reference */
  private scaleManager: UIScaleManager;

  constructor(config: Partial<FuelBarConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.scaleManager = UIScaleManager.getInstance();
    this.container = new Container();
    this.container.label = 'FuelBar';

    // Create background
    this.background = new Graphics();
    this.container.addChild(this.background);

    // Create fill
    this.fill = new Graphics();
    this.container.addChild(this.fill);

    // Create border
    this.border = new Graphics();
    this.container.addChild(this.border);

    // Create label
    this.label = new Text({
      text: 'FUEL',
      style: new TextStyle({
        fontFamily: 'Arial',
        fontSize: this.scaleManager.scaleFontSize(10),
        fontWeight: 'bold',
        fill: 0x4ecdc4,
        letterSpacing: 1,
      }),
    });
    this.label.anchor.set(0.5, 0.5);
    this.container.addChild(this.label);

    this.draw();

    // Listen for scale changes
    this.scaleManager.addListener(() => this.applyScaling());
  }

  /**
   * Apply current scaling to all elements
   */
  private applyScaling(): void {
    (this.label.style as TextStyle).fontSize = this.scaleManager.scaleFontSize(10);
    this.draw();
  }

  /**
   * Update the fuel display
   */
  update(fuel: number): void {
    this.currentFuel = Math.max(0, Math.min(FUEL.MAX_FUEL, fuel));
    this.draw();
  }

  /**
   * Draw the fuel bar
   */
  private draw(): void {
    const { width, height } = this.config;
    const fuelPercent = this.currentFuel / FUEL.MAX_FUEL;
    const cornerRadius = height / 2;

    // Determine color based on fuel level
    let fillColor: number;
    let glowAlpha: number;
    if (fuelPercent > FUEL_THRESHOLDS.HIGH) {
      fillColor = 0x4ecdc4; // Teal when high
      glowAlpha = 0.3;
    } else if (fuelPercent > FUEL_THRESHOLDS.MEDIUM) {
      fillColor = 0xf9ca24; // Yellow when medium
      glowAlpha = 0.4;
    } else {
      fillColor = 0xff6b6b; // Red when low
      glowAlpha = 0.5;
    }

    // Draw background
    this.background.clear();
    this.background.roundRect(-width / 2, -height / 2, width, height, cornerRadius);
    this.background.fill({ color: 0x1a1a2e, alpha: 0.85 });

    // Draw fuel fill
    this.fill.clear();
    if (fuelPercent > 0) {
      const fillWidth = (width - 4) * fuelPercent;
      this.fill.roundRect(-width / 2 + 2, -height / 2 + 2, fillWidth, height - 4, cornerRadius - 2);
      this.fill.fill({ color: fillColor, alpha: 0.9 });

      // Add inner glow
      this.fill.roundRect(-width / 2 + 2, -height / 2 + 2, fillWidth, (height - 4) * 0.4, cornerRadius - 2);
      this.fill.fill({ color: 0xffffff, alpha: 0.2 });
    }

    // Draw border
    this.border.clear();
    this.border.roundRect(-width / 2, -height / 2, width, height, cornerRadius);
    this.border.stroke({ color: fillColor, width: 1.5, alpha: 0.8 });

    // Outer glow when low
    if (fuelPercent < FUEL_THRESHOLDS.MEDIUM) {
      this.border.roundRect(-width / 2 - 2, -height / 2 - 2, width + 4, height + 4, cornerRadius + 2);
      this.border.stroke({ color: fillColor, width: 2, alpha: glowAlpha });
    }

    // Position label to the left of bar
    this.label.position.set(-width / 2 + LABEL_OFFSET, 0);
  }

  /**
   * Handle screen resize
   */
  onResize(width: number, height: number): void {
    // Position at bottom center of screen
    this.container.position.set(
      width / 2,
      height - this.config.bottomPadding
    );
  }

  /**
   * Show the fuel bar
   */
  show(): void {
    this.container.visible = true;
  }

  /**
   * Hide the fuel bar
   */
  hide(): void {
    this.container.visible = false;
  }

  /**
   * Get current fuel level
   */
  getFuel(): number {
    return this.currentFuel;
  }

  /**
   * Check if fuel is low (below 25%)
   */
  isLow(): boolean {
    return this.currentFuel < FUEL.MAX_FUEL * FUEL_THRESHOLDS.MEDIUM;
  }

  /**
   * Check if fuel is empty
   */
  isEmpty(): boolean {
    return this.currentFuel <= 0;
  }
}
